// PillsV2 — alternative top-row layout for the in-suite dashboard.
// Mirrors the Valve Audit page: two wide hero pills on the left (Total Inspected,
// Corrective Repairs Needed) and two narrow pills on the right (Missed Units,
// Overall Health). Designed to be a drop-in replacement for the original
// `Pills` component — pass the same `stats` plus `unitsData` so we can derive
// the missed-unit count.
//
// To revert to the original layout: import `Pills` again from `./Pills` in
// Dashboard.js and pass only `{ stats, hvacType }`.

import React from 'react';
import { Card } from 'react-bootstrap';
import {
  FaBuilding,
  FaWrench,
  FaBan,
  FaHeartbeat,
  FaCheckCircle,
} from 'react-icons/fa';
import './Pills.css';

const getHealthColor = (p) => {
  if (p >= 80) return '#28a745';
  if (p >= 60) return '#ffc107';
  if (p >= 40) return '#fd7e14';
  return '#dc3545';
};

const HealthCircle = ({ percentage, size = 95 }) => {
  const stroke = 9;
  const radius = (size - stroke) / 2;
  const circumference = 2 * Math.PI * radius;
  const dashOffset = circumference - (percentage / 100) * circumference;
  const color = getHealthColor(percentage);
  const c = size / 2;
  return (
    <div style={{ position: 'relative', width: size, height: size }}>
      <svg width={size} height={size}>
        <circle cx={c} cy={c} r={radius} fill="none" stroke="#e9ecef" strokeWidth={stroke} />
        <circle
          cx={c}
          cy={c}
          r={radius}
          fill="none"
          stroke={color}
          strokeWidth={stroke}
          strokeDasharray={circumference}
          strokeDashoffset={dashOffset}
          strokeLinecap="round"
          transform={`rotate(-90 ${c} ${c})`}
        />
      </svg>
      <div
        style={{
          position: 'absolute',
          top: '50%',
          left: '50%',
          transform: 'translate(-50%, -50%)',
          fontSize: size <= 85 ? '1.4rem' : '1.9rem',
          fontWeight: 800,
          color: '#1e293b',
          letterSpacing: '-0.01em',
        }}
      >
        {percentage}%
      </div>
    </div>
  );
};

export function SmallStatPill({
  title,
  titleClass,
  icon,
  iconClass,
  iconColor,
  value,
  valueColor = '#1e293b',
  label,
  cta,
}) {
  return (
    <Card className="shadow-sm border-0 pill-card pill-card-total h-100">
      <Card.Body className="pill-body pill-body-total d-flex flex-column">
        <div className="pill-header-row pill-header-row-total">
          <Card.Title className={`pill-title ${titleClass || ''} mb-0`}>{title}</Card.Title>
          <div
            className={`pill-icon ${iconClass || ''} pill-icon-inline`}
            style={iconColor ? { color: iconColor } : undefined}
          >
            {icon}
          </div>
        </div>
        <div className="pill-stat-content" style={{ marginTop: '1.4rem' }}>
          <div
            className="pill-stat-value"
            style={{
              fontSize: '3rem',
              fontWeight: 800,
              color: valueColor,
              lineHeight: 1,
              letterSpacing: '-0.02em',
            }}
          >
            {value}
          </div>
          {label && (
            <div
              style={{
                fontSize: '0.85rem',
                fontWeight: 500,
                color: '#94a3b8',
                marginTop: '0.45rem',
                lineHeight: 1.2,
              }}
            >
              {label}
            </div>
          )}
          {cta && (
            <button
              type="button"
              onClick={cta.onClick}
              className="pill-cta-link pill-cta-link--inline"
            >
              {cta.label}
              <span className="pill-cta-arrow" aria-hidden="true">→</span>
            </button>
          )}
        </div>
      </Card.Body>
    </Card>
  );
}

function SmallHealthPill({ title, percentage }) {
  return (
    <Card className="shadow-sm border-0 pill-card pill-card-total h-100">
      <Card.Body className="pill-body pill-body-total d-flex flex-column">
        <div className="pill-header-row pill-header-row-total">
          <Card.Title className="pill-title pill-title-health mb-0">{title}</Card.Title>
          <div
            className="pill-icon pill-icon-health pill-icon-inline"
            style={{ color: getHealthColor(percentage) }}
          >
            <FaHeartbeat />
          </div>
        </div>
        <div style={{ flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center', paddingTop: '0.4rem' }}>
          <HealthCircle percentage={percentage} size={78} />
        </div>
      </Card.Body>
    </Card>
  );
}

function PillsV2({ stats = {}, unitsData = [], hvacType, onShowMissed }) {
  const totalUnits = stats.totalUnits || 0;
  const noIssues = stats.noIssues || 0;
  const foundIssues = stats.foundIssues || 0;

  // Missed isn't in stats; derive from unitsData (backend now includes Missed rows).
  const missedCount = (unitsData || []).filter((r) => r.inspection === 'Missed').length;

  const healthPct = totalUnits > 0 ? Math.round((noIssues / totalUnits) * 100) : 0;

  const equipmentLabel =
    hvacType === 'heatpump' ? 'Heatpumps'
      : hvacType === 'dryer-vent' ? 'Vents'
      : 'Fancoils';
  const totalTitle = `Total ${equipmentLabel} Inspected`;
  const healthTitle =
    hvacType === 'heatpump' ? 'Heatpump Health'
      : hvacType === 'dryer-vent' ? 'Vent Health'
      : 'Fancoil Health';

  const showMissed = missedCount > 0;

  return (
    <div className="pills-container" data-tour="pills-stats">
      <div className={`pillsv2-grid${showMissed ? '' : ' pillsv2-grid--no-missed'}`}>
        {/* Pill 1 — Total Inspected (wide) */}
        <SmallStatPill
          title={totalTitle}
          titleClass="pill-title-total"
          icon={<FaBuilding />}
          iconColor="#2563eb"
          value={totalUnits}
          valueColor="#1e293b"
          label={`${equipmentLabel.toLowerCase()} inspected`}
        />

        {/* Pill 2 — No Issues (wide, green) */}
        <SmallStatPill
          title="No Issues"
          icon={<FaCheckCircle />}
          iconColor="#16a34a"
          value={noIssues}
          valueColor="#16a34a"
          label={`${equipmentLabel.toLowerCase()} OK`}
        />

        {/* Pill 3 — Repairs Needed (wide, red) */}
        <SmallStatPill
          title="Repairs Needed"
          titleClass="pill-title-found"
          icon={<FaWrench />}
          iconColor="#dc3545"
          value={foundIssues}
          valueColor="#dc3545"
          label="units have deficiencies"
        />

        {/* Pill 4 — No Access Units (narrow) — only shown when there are any */}
        {showMissed && (
          <SmallStatPill
            title="No Access Units"
            titleClass="pill-title-found"
            iconClass="pill-icon-missed"
            icon={<FaBan />}
            value={missedCount}
            valueColor="#d97706"
            cta={
              typeof onShowMissed === 'function'
                ? { label: 'See No Access Units', onClick: onShowMissed }
                : null
            }
          />
        )}

        {/* Pill 5 — Overall Health (narrow) */}
        <SmallHealthPill title={healthTitle} percentage={healthPct} />
      </div>
    </div>
  );
}

export default PillsV2;
